"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMedications,
  createMedication,
  updateMedication,
  deleteMedication,
} from "@/lib/api/medications";
import type { UpdateMedicationRequest } from "@/types/medication";
import { toast } from "sonner";

export function useMedications() {
  return useQuery({
    queryKey: ["medications"],
    queryFn: getMedications,
  });
}

export function useCreateMedication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMedication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications"] });
      toast.success("Medication added");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useUpdateMedication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateMedicationRequest;
    }) => updateMedication(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications"] });
      toast.success("Medication updated");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useDeleteMedication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMedication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications"] });
      toast.success("Medication removed");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
